import { Button } from '@progress/kendo-react-buttons'
import { SearchBody } from '../../../components/layouts'
import { StatusCheckCondition } from './StatusCheckCondition'
import StatusCheckResultList from './StatusCheckResultList'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import { StatusCheckGateway } from '../../../gateway/supplier/StatusCheckGateway'
import { PutSupplierHearingStatusItemGateway } from '../../../gateway/supplier/PutSupplierHearingStatusItemGateway'
import { PostSupplierHearingMailGateway } from '../../../gateway/supplier/PostSupplierHearingMailGateway'
import { DeleteSupplierHearingStatusItemGateway } from '../../../gateway/supplier/DeleteSupplierHearingStatusItemGateway'
import { PostReportYamadumiSupplierGateway } from '../../../gateway/supplier/PostReportYamadumiSupplierGateway'
import { FC, useEffect, useState } from 'react'
import {
  PostSupplierHearingMailReqParams,
  PostReportYamadumiSupplierReqParams,
  PutSupplierHearingStatusItemReqParams
} from '@gateway.Supplier'

import { ConfirmDialog, ErrorMessages, ErrorMessage } from '../../../components/parts'
import { useNavigate } from 'react-router-dom'
import { BlobFileService, MessageProvider } from '../../../services'
import {
  useAppSelector,
  useAppDispatch,
  StatusCheckActions,
  CommonActions
} from '../../../stores'
import { Authority } from '../../../gateway/system/Authority'

export interface StatusCheckProps {
  /** シミュレーション年月度 */
  simulationYm: string
  /** 回答年月 */
  replyYm: string
  /** 費目 */
  expense: string
  /** 費目コード */
  expenseCd: string
  /** 回答状況 */
  replySts: string
  /** 回答ステータスコード */
  replyStsCd: string
  /** 支店名 */
  branchNm: string
  /** 支店名コード */
  branchCd: string
  /** 業者名 */
  supplierNm: string
  /** 業者名コード */
  supplierCd: string
  /** 業者支店名 */
  supplierBranchNm: string | null
  /** 業者支店コード */
  supplierBranchCd: string
  /** 工場名 */
  factoryNm: string
  /** 工場名コード */
  factoryCd: string
  /** 回答依頼日 */
  replyReqYmd: string
  /** 回答依頼者 */
  replyReqPerson: string
  /** 回答日 */
  replyYmd: string
  /** 回答者 */
  replyPerson: string
  /** 確認日 */
  confirmationYmd: string
  /** 確認者 */
  confirmationPerson: string
  /** 回答依頼フラグ */
  replyReqFlg: number
  /** ヒアリングシートフラグ */
  hearingSheetFlg: number
  /** ヒアリングシート対象フラグ */
  hearingSheetTargetFlg: number
  /** 更新日時 */
  updDtm: string | null
  /** 出力対象フラグ */
  outPutFlg: number
}

export interface StatusCheckListType {
  statusCheckList: StatusCheckProps[]
}

export const StatusCheck: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  const beforePageTitle = usePageTitle('業者ヒアリング状況確認')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)
  /** データストアで状態管理している検索条件 */
  const statusCheckSearchData = useAppSelector(
    (state) => state.statuscheck.statusCheckSearch
  )
  const [confirmDialogShow, setConfirmDialogShow] = useState(false)
  const [messageId, setMessageId] = useState('')
  const [messageArgslist, setMessageArgslist] = useState([''])
  const [demandIndex, setDemandIndex] = useState(0)
  const [cancelIndex, setCancelIndex] = useState(0)

  const statusCheckList = useAppSelector(
    (state) => state.statuscheck.statusCheckList
  )
  const skip = useAppSelector(
    (state) => state.statuscheck.skip
  )
  const target = useAppSelector(
    (state) => state.statuscheck.target
  )

  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const userProp = useAppSelector((state) => state.common.userProp)
  const [isChanged, setIsChanged] = useState(false)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(statusCheckSearchData.simulationY)
  const simulatedMonth = Number(statusCheckSearchData.simulationM)
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  /** 初期 */
  const statusCheckInfo = {
    simulationM: String(new Date().getMonth() + 1),
    simulationY: String(new Date().getFullYear()),
    branchCdList: userProp !== undefined && userProp.authority !== Authority.Supplier ? [userProp.branchCode] : [],
    expenseCd: '',
    supplierNm: '',
    replyStsCdList: [] as string[],
    sortOrder: '1',
    matrixTransitionFlg: false
  }
  type StatusCheckInfoType = typeof statusCheckInfo

  /**
   * 初期検索を実行する
   */
  useEffect(() => {
    // 検索結果のストアを空にする
    dispatch(StatusCheckActions.setStatusCheckList([]))
    let condition: StatusCheckInfoType = statusCheckInfo
    let tempSkip = 0
    let tempTarget = ''
    if (beforePageTitle === '業者ヒアリング回答確認' || statusCheckSearchData.matrixTransitionFlg) {
      // トップから遷移してきた場合のみ、明示的にフラグをオフにする
      condition = statusCheckSearchData.matrixTransitionFlg ? { ...statusCheckSearchData, matrixTransitionFlg: false } : statusCheckSearchData
      // 業者ヒアリング回答確認画面から遷移してきた場合は、ストアに保持している検索条件を設定する
      dispatch(
        StatusCheckActions.setStatusCheckSearch(condition)
      )
      // トップから遷移してきた場合のみ、スキップ明細数を初期状態に設定する
      tempSkip = statusCheckSearchData.matrixTransitionFlg ? 0 : skip
      // 業者ヒアリング回答確認画面から遷移してきた場合は、ストアに保持しているスキップ明細数を設定する
      dispatch(StatusCheckActions.setSkip(tempSkip))
      // トップから遷移してきた場合のみ、ターゲットを初期状態に設定する
      tempTarget = statusCheckSearchData.matrixTransitionFlg ? '' : target
      // 業者ヒアリング回答確認画面から遷移してきた場合は、ストアに保持しているターゲットを設定する
      dispatch(StatusCheckActions.setTarget(tempTarget))
    } else {
      // 検索条件を明示的に初期状態に設定する
      dispatch(
        StatusCheckActions.setStatusCheckSearch(condition)
      )
      // スキップ明細数を明示的に初期状態に設定する
      dispatch(StatusCheckActions.setSkip(tempSkip))
      // ターゲットを明示的に初期状態に設定する
      dispatch(StatusCheckActions.setTarget(tempTarget))
    }
    // 検索を実行する
    apiStatusCheckResultGateWay({
      ...condition,
      simulationM:
        Number(condition.simulationM) > 9
          ? condition.simulationM
          : `0${condition.simulationM}`
    })
  }, [])

  /**
   * クリアボタンイベント
   */
  const handleClear = () => {
    dispatch(StatusCheckActions.setStatusCheckSearch(statusCheckInfo))
  }

  /**
   * 検索ボタンイベント
   */
  const handleSearch = () => {
    dispatch(StatusCheckActions.setSkip(0))
    dispatch(StatusCheckActions.setTarget(''))
    searchEventStore()
  }

  /**
   * 検索ボタンイベント
   * データストアで状態管理している検索条件を使って検索する（再検索時に使用する）
   */
  const searchEventStore = () => {
    apiStatusCheckResultGateWay({
      ...statusCheckSearchData,
      simulationM:
        Number(statusCheckSearchData.simulationM) > 9
          ? statusCheckSearchData.simulationM
          : `0${statusCheckSearchData.simulationM}`
    })
  }

  /**
   * 業者ヒアリング状況一覧の取得
   */
  const apiStatusCheckResultGateWay = (req: {
    simulationY: string
    simulationM: string
    branchCdList?: string[]
    expenseCd?: string
    supplierNm?: string
    replyStsCdList?: string[]
    sortOrder: string
  }) => {
    dispatch(CommonActions.setIsLoading(true))
    StatusCheckGateway.getStatusCheckResultList(req)
      .then((res) => {
        if (res.error) {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
          dispatch(StatusCheckActions.setStatusCheckList([]))
        } else {
          // 検索結果存在チェック
          if (res.data.supplierHearingList.length === 0) {
            const message: ErrorMessage[] = [{
              id: 'I001',
              content: MessageProvider.provide(
                'I001', ...[]
              )
            }]
            dispatch(StatusCheckActions.setStatusCheckList([]))
            setNotificationMessages(message)
          } else if (res.data.supplierHearingList.length > 1000) {
            // 1000件以上の場合
            const message: ErrorMessage[] = [{
              id: 'I015',
              content: MessageProvider.provide(
                'I015', ...['1000']
              )
            }]
            setNotificationMessages(message)
            dispatch(
              StatusCheckActions.setStatusCheckList(
                res.data.supplierHearingList.slice(0, 1000).map((item) => {
                  return {
                    ...item,
                    outPutFlg: 0,
                    // 支店担当者の場合、他支店管轄の業者の明細は回答依頼フラグの初期値が1の時に0にする
                    replyReqFlg: item.replyReqFlg === 1 && userProp?.authority === Authority.Branch && userProp?.branchCode !== item.branchCd ? 0 : item.replyReqFlg
                  }
                })
              )
            )
          } else {
            // 1~1000件以内の場合
            dispatch(
              StatusCheckActions.setStatusCheckList(
                res.data.supplierHearingList.map((item) => {
                  return {
                    ...item,
                    outPutFlg: 0,
                    // 支店担当者の場合、他支店管轄の業者の明細は回答依頼フラグの初期値が1の時に0にする
                    replyReqFlg: item.replyReqFlg === 1 && userProp?.authority === Authority.Branch && userProp?.branchCode !== item.branchCd ? 0 : item.replyReqFlg
                  }
                })
              )
            )
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  /**
   * 戻る
   */
  const handleGoBack = () => {
    navigate(-1)
  }

  /**
   * 出力ボタン押下
   */
  const handleOutputBtn = () => {
    setMessageId('I007')
    setMessageArgslist(['業者山積表の出力'])
    setConfirmDialogShow(true)
  }

  /**
   * 出力処理
   */
  const handleOutput = () => {
    // 現在のページ・データの取得
    const needStatusCheckList = []
    for (const item of statusCheckList.slice(skip, skip + 20)) {
      if (item.outPutFlg === 1) {
        needStatusCheckList.push(item)
      }
    }

    // 出力対象が0件の場合、エラーとする
    if (needStatusCheckList.length === 0) {
      setErrorMessages([{
        id: 'E024',
        content: MessageProvider.provide(
          'E024',
          '出力対象の業者'
        )
      }])
      return
    }

    // 必要なパラメータの取得
    const reqParams: PostReportYamadumiSupplierReqParams = {
      data: {
        outputTargetList: new Array<{simulationYm?: string
          supplierCd: string
          supplierNm?: string
          supplierBranchCd: string
          supplierBranchNm?: string | null
          factoryCd?: string
          factoryNm?: string
          expenseCd: string
          expenseNm?: string
          replyStsCd: string}>(),
        displayStartYear
      }

    }
    for (let i = 0; i < needStatusCheckList.length; i++) {
      const reqParamsItem = {
        simulationYm: '',
        supplierCd: '',
        supplierNm: '',
        supplierBranchCd: '',
        supplierBranchNm: '',
        factoryCd: '',
        factoryNm: '',
        expenseCd: '',
        expenseNm: '',
        replyStsCd: ''
      }
      reqParamsItem.simulationYm = needStatusCheckList[i].simulationYm
      reqParamsItem.supplierCd = needStatusCheckList[i].supplierCd
      reqParamsItem.supplierNm = needStatusCheckList[i].supplierNm
      reqParamsItem.supplierBranchCd = needStatusCheckList[i].supplierBranchCd
      reqParamsItem.supplierBranchNm = needStatusCheckList[i].supplierBranchNm ?? ''
      reqParamsItem.factoryCd = needStatusCheckList[i].factoryCd
      reqParamsItem.factoryNm = needStatusCheckList[i].factoryNm
      reqParamsItem.expenseCd = needStatusCheckList[i].expenseCd
      reqParamsItem.expenseNm = needStatusCheckList[i].expense
      reqParamsItem.replyStsCd = needStatusCheckList[i].replyStsCd
      reqParams.data.outputTargetList.push(reqParamsItem)
    }

    //  業者山積表出力API
    dispatch(CommonActions.setIsLoading(true))
    PostReportYamadumiSupplierGateway.postReportSupplier(reqParams)
      .then((res) => {
        if (!res.error) {
          res.data.downloadPass.forEach((filePath) => {
            if (typeof filePath === 'string') {
              BlobFileService.downloadRedirectTo(filePath)
              const message: ErrorMessage[] = [{
                id: 'I004',
                content: MessageProvider.provide(
                  'I004', ...['業者山積表出力']
                )
              }]
              if (isChanged) {
                setIsChanged(false) // TODO forwardGuardで遷移する際に差分検知として扱わせないための処理
                resetLeaveGuard()
              }
              setNotificationMessages(message)
            }
          })
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  /** 引継ぎボタン押下  */
  const handleTakeOverBtn = () => {
    // 引継ぎ対象存在チェック
    let requestTarget = 0
    for (const item of statusCheckList.slice(skip, skip + 20)) {
      if (item.replyReqFlg === 1 && item.replyStsCd === '1') {
        requestTarget++
      }
    }

    if (requestTarget === 0) {
      setErrorMessages([{
        id: 'E024',
        content: MessageProvider.provide(
          'E024',
          '引継ぎ対象の業者'
        )
      }])
    } else {
      // 確認ポップアップを表示
      setMessageId('I007')
      setMessageArgslist(['引継ぎ'])
      setConfirmDialogShow(true)
    }
  }

  /** 回答依頼ボタン押下  */
  const handleRequestBtn = () => {
    // 依頼対象存在チェック
    let requestTarget = 0
    for (const item of statusCheckList.slice(skip, skip + 20)) {
      if (item.replyReqFlg === 1 && (item.replyStsCd === '1' || item.replyStsCd === '8')) {
        requestTarget++
      }
    }

    if (requestTarget === 0) {
      setErrorMessages([{
        id: 'E024',
        content: MessageProvider.provide(
          'E024',
          '依頼対象の業者'
        )
      }])
    } else {
      // 確認ポップアップを表示
      setMessageId('I007')
      setMessageArgslist(['回答依頼'])
      setConfirmDialogShow(true)
    }
  }

  /**
   * 業者ヒアリング状況確認-メール送信API
   */
  const apiGetHearingMailSendGatway = (
    reqParams: PostSupplierHearingMailReqParams
  ) => {
    dispatch(CommonActions.setIsLoading(true))
    PostSupplierHearingMailGateway.postSupplierHearingMail(reqParams)
      .then((res) => {
        if (!res.error) {
          searchEventStore()
          if (isChanged) {
            setIsChanged(false) // TODO forwardGuardで遷移する際に差分検知として扱わせないための処理
          }
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  /** ConfirmDialog イベントの確認 */
  const dialogConfirmHandleBtn = () => {
    setConfirmDialogShow(false)
    if (messageArgslist[0] === '督促') {
      const message: ErrorMessage[] = [{
        id: 'I004',
        content: MessageProvider.provide(
          'I004', ...['督促']
        )
      }]
      setNotificationMessages(message)
      const reqParams: PutSupplierHearingStatusItemReqParams = {
        data: {
          supplierHearingList: [
            {
              simulationYm: statusCheckList[demandIndex].simulationYm,
              expenseCd: statusCheckList[demandIndex].expenseCd,
              replyStsCd: statusCheckList[demandIndex].replyStsCd,
              supplierCd: statusCheckList[demandIndex].supplierCd,
              supplierBranchCd: statusCheckList[demandIndex].supplierBranchCd,
              factoryCd: statusCheckList[demandIndex].factoryCd,
              updDtm: statusCheckList[demandIndex].updDtm
            }
          ],
          replyStsCdAfterChange: '4',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            apiGetHearingMailSendGatway({ data: res.data })
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '取消') {
      const message: ErrorMessage[] = [{
        id: 'I004',
        content: MessageProvider.provide(
          'I004', ...['取消']
        )
      }]
      setNotificationMessages(message)
      const reqParams = {
        simulationYm: statusCheckList[cancelIndex].simulationYm,
        supplierCd: statusCheckList[cancelIndex].supplierCd,
        supplierBranchCd: statusCheckList[cancelIndex].supplierBranchCd,
        factoryCd: statusCheckList[cancelIndex].factoryCd,
        expenseCd: statusCheckList[cancelIndex].expenseCd
      }
      dispatch(CommonActions.setIsLoading(true))
      DeleteSupplierHearingStatusItemGateway.deleteSupplierHearingStatusItem(
        reqParams.simulationYm,
        reqParams.supplierCd,
        reqParams.supplierBranchCd,
        reqParams.factoryCd,
        reqParams.expenseCd
      )
        .then((res) => {
          if (!res.error) {
            // 取消の場合はメールの送信は行わない
            // apiGetHearingMailSendGatway({ data: res.data })

            // 再検索
            searchEventStore()
            if (isChanged) {
              setIsChanged(false) // forwardGuardで遷移する際に差分検知として扱わせないための処理
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '業者山積表の出力') {
      // 帳票出力
      handleOutput()
    } else if (messageArgslist[0] === '引継ぎ') {
      // 引継ぎ
      // 現在のページ・データの取得
      const needStatusCheckList = []
      for (const item of statusCheckList.slice(skip, skip + 20)) {
        if (item.replyReqFlg === 1 && item.updDtm === null) {
          needStatusCheckList.push(item)
        }
      }
      // 必要なパラメータの取得
      const reqParamsSupplierHearingList: PutSupplierHearingStatusItemReqParams['data']['supplierHearingList'] = []
      for (let i = 0; i < needStatusCheckList.length; i++) {
        const reqParamsItem = {
          simulationYm: '',
          expenseCd: '',
          replyStsCd: '',
          supplierCd: '',
          supplierBranchCd: '',
          factoryCd: '',
          replyReqFlg: 0,
          hearingSheetFlg: 0,
          replyReqPerson: '',
          updDtm: null as string | null
        }
        reqParamsItem.simulationYm = needStatusCheckList[i].simulationYm
        reqParamsItem.expenseCd = needStatusCheckList[i].expenseCd
        reqParamsItem.replyStsCd = needStatusCheckList[i].replyStsCd
        reqParamsItem.supplierCd = needStatusCheckList[i].supplierCd
        reqParamsItem.supplierBranchCd = needStatusCheckList[i].supplierBranchCd
        reqParamsItem.expenseCd = needStatusCheckList[i].expenseCd
        reqParamsItem.factoryCd = needStatusCheckList[i].factoryCd
        reqParamsItem.replyReqFlg = needStatusCheckList[i].replyReqFlg
        reqParamsItem.hearingSheetFlg = needStatusCheckList[i].hearingSheetFlg
        reqParamsItem.replyReqPerson = needStatusCheckList[i].replyReqPerson
        reqParamsItem.updDtm = needStatusCheckList[i].updDtm
        reqParamsSupplierHearingList.push(reqParamsItem)
      }
      const reqParams: PutSupplierHearingStatusItemReqParams = {
        data: {
          supplierHearingList: reqParamsSupplierHearingList,
          replyStsCdAfterChange: '7',
          takeBackFlg: 0,
          takeOverFlg: 1,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', ...['引継ぎ']
              )
            }]
            if (isChanged) {
              resetLeaveGuard()
            }
            setNotificationMessages(message)
            searchEventStore()
            if (isChanged) {
              setIsChanged(false) // TODO forwardGuardで遷移する際に差分検知として扱わせないための処理
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    } else if (messageArgslist[0] === '回答依頼') {
      // 回答依頼
      // 現在のページ・データの取得
      const needStatusCheckList = []
      for (const item of statusCheckList.slice(skip, skip + 20)) {
        if (item.replyReqFlg === 1 && (item.replyStsCd === '1' || item.replyStsCd === '8')) {
          needStatusCheckList.push(item)
        }
      }
      // 必要なパラメータの取得
      const reqParamsSupplierHearingList: PutSupplierHearingStatusItemReqParams['data']['supplierHearingList'] = []
      for (let i = 0; i < needStatusCheckList.length; i++) {
        const reqParamsItem = {
          simulationYm: '',
          expenseCd: '',
          replyStsCd: '',
          supplierCd: '',
          supplierBranchCd: '',
          factoryCd: '',
          replyReqFlg: 0,
          hearingSheetFlg: 0,
          replyReqPerson: '',
          updDtm: null as string | null
        }
        reqParamsItem.simulationYm = needStatusCheckList[i].simulationYm
        reqParamsItem.expenseCd = needStatusCheckList[i].expenseCd
        reqParamsItem.replyStsCd = needStatusCheckList[i].replyStsCd
        reqParamsItem.supplierCd = needStatusCheckList[i].supplierCd
        reqParamsItem.supplierBranchCd = needStatusCheckList[i].supplierBranchCd
        reqParamsItem.expenseCd = needStatusCheckList[i].expenseCd
        reqParamsItem.factoryCd = needStatusCheckList[i].factoryCd
        reqParamsItem.replyReqFlg = needStatusCheckList[i].replyReqFlg
        reqParamsItem.hearingSheetFlg = needStatusCheckList[i].hearingSheetFlg
        reqParamsItem.replyReqPerson = needStatusCheckList[i].replyReqPerson
        reqParamsItem.updDtm = needStatusCheckList[i].updDtm
        reqParamsSupplierHearingList.push(reqParamsItem)
      }
      const reqParams: PutSupplierHearingStatusItemReqParams = {
        data: {
          supplierHearingList: reqParamsSupplierHearingList,
          replyStsCdAfterChange: '2',
          takeBackFlg: 0,
          takeOverFlg: 0,
          fixContentFlg: 0
        }
      }
      dispatch(CommonActions.setIsLoading(true))
      PutSupplierHearingStatusItemGateway.putHearingStatusItem(reqParams)
        .then((res) => {
          if (!res.error) {
            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', ...['回答依頼']
              )
            }]
            if (isChanged) {
              resetLeaveGuard()
            }
            setNotificationMessages(message)
            apiGetHearingMailSendGatway({ data: res.data })
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
  }

  const setSkip = (skip: number) => {
    dispatch(StatusCheckActions.setSkip(skip))
  }

  return (
    <>
      <SearchBody
        total={statusCheckList.length}
        changeSkip={setSkip}
        skip={skip}
        leftPane={
          <StatusCheckCondition/>
        }
        rightPane={
          <StatusCheckResultList
            setIsChanged={setIsChanged}
            isChanged={isChanged}
            changeId={setMessageId}
            changeArgslist={setMessageArgslist}
            changesDemandIndex={setDemandIndex}
            changeCancelIndex={setCancelIndex}
            changeMessageDialogshow={setConfirmDialogShow}
            skip={skip}
            setErrorMessages={setErrorMessages}
          />
        }
        leftPaneButton={
          <>
            <Button onClick={handleClear}>クリア</Button>
            <Button themeColor="primary" onClick={handleSearch}>
              検索
            </Button>
          </>
        }
        rightPaneButton={
          <>
            <Button onClick={handleGoBack}>戻る</Button>
            <Button onClick={handleOutputBtn} themeColor="primary">
              出力
            </Button>
            {(userProp?.authority !== 5 && !lastMonthFlg)
              ? (
                <Button
                  onClick={handleTakeOverBtn}
                  themeColor="primary"
                >
                  引継ぎ
                </Button>
                )
              : null
            }
            {(userProp?.authority !== 5 && !lastMonthFlg)
              ? (
                <Button
                  onClick={handleRequestBtn}
                  themeColor="primary"
                >
                  回答依頼
                </Button>
                )
              : null
            }
          </>
        }
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
      <ConfirmDialog
        open={confirmDialogShow}
        renderCloseButton
        id={messageId}
        args={messageArgslist}
        onClickOk={dialogConfirmHandleBtn}
        onClickCancel={() => setConfirmDialogShow(false)}
      />
    </>
  )
}
